import { Inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConfigService } from './config.service';
import { Auth0Group, BasicResponse, GidAccessControl } from '../..';

@Injectable({
  providedIn: 'root',
})
export class UserAccessControlService {
  private URL;
  headers: HttpHeaders;

  constructor(
    @Inject(ConfigService) private config,
    private httpClient: HttpClient,
  ) {
    this.URL =
      config.REST_SERVER_HOST +
      config.REST_SERVER_url.find((restUrl) => restUrl.api === 'PADM').url;
    this.headers = new HttpHeaders().set('DoNotIntercept', 'true');
  }

  private static handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`,
      );
    }
    // return an observable with a user-facing error message
    // return throwError('Something bad happened; please try again later.');
    return throwError(error);
  }

  /**
   * PADM NewAccessControlRequest / put-new-access-control requires portalRegisterId; ids must be JSON numbers.
   */
  private static normalizePortalRegisterIdForApi(
    portalRegisterId: GidAccessControl['portalRegisterId'] | undefined,
  ): { id: number }[] {
    if (!Array.isArray(portalRegisterId)) {
      return [];
    }
    return portalRegisterId
      .map((row) => ({
        id: Number((row as { id: unknown }).id),
      }))
      .filter((row) => Number.isFinite(row.id));
  }

  // See C001 - 2026-01-27 - HE: id in URL, omitted from body
  // See C003 - 2026-04-15 - HE
  // updateAccessControl(
  //   accessControl: GidAccessControl,
  // ): Observable<BasicResponse> {
  //   ...
  //   const body = { ...accessControl } as Record<string, unknown>;
  //   delete body['id'];
  //   return this.httpClient.put ...
  // }
  updateAccessControl(
    accessControl: GidAccessControl,
  ): Observable<BasicResponse> {
    console.log('==========================================================');
    console.log('accessControl');
    console.log(accessControl);
    console.log('==========================================================');
    const headers = this.headers;

    const id = accessControl.id;
    const body = {
      // portalRegisterId: Number(accessControl.portalRegisterId),
      uacId: accessControl.uacId,
      defaultGid: accessControl.defaultGid,
      // defaultSPS: accessControl.defaultSPS,
      accessControl: accessControl.accessControl,
      selectionList: accessControl.selectionList,
      exclusionList: accessControl.exclusionList,
    };
    return this.httpClient
      .put<BasicResponse>(`${this.URL}/accesscontrol/access/${id}`, body, {
        headers,
      })
      .pipe(catchError(UserAccessControlService.handleError));
  }

  // See C003 - 2026-04-15 - HE
  // createAccessControl(...): body omitted portalRegisterId (commented), causing PADM 9405 Required field not present portalRegisterId on put-new-access-control.
  createAccessControl(
    accessControl: GidAccessControl,
  ): Observable<BasicResponse> {
    const headers = this.headers;
    const body = {
      portalRegisterId: accessControl.portalRegisterId?.[0]?.id,
      uacId: accessControl.uacId,
      defaultGid: accessControl.defaultGid,
      // defaultSPS: accessControl.defaultSPS,
      accessControl: accessControl.accessControl,
      selectionList: accessControl.selectionList,
      exclusionList: accessControl.exclusionList,
    };
    return this.httpClient
      .put<BasicResponse>(`${this.URL}/accesscontrol/access/`, body, {
        headers,
      })
      .pipe(catchError(UserAccessControlService.handleError));
  }

  getUserPortalAuthGroups(
    uacID: number,
    portalID: number,
  ): Observable<Auth0Group[]> {
    const headers = this.headers;
    return this.httpClient
      .get<
        Auth0Group[]
      >(`${this.URL}/accesscontrol/access/user/${uacID}/portal/${portalID}/authgroups`, { headers })
      .pipe(catchError(UserAccessControlService.handleError));
  }
  updateUserAccessGroup(
    userAccessGroup: Auth0Group,
  ): Observable<BasicResponse> {
    const headers = this.headers;
    return this.httpClient
      .patch<BasicResponse>(
        `${this.URL}/accesscontrol/access/user/accessgroup`,
        userAccessGroup,
        { headers },
      )
      .pipe(catchError(UserAccessControlService.handleError));
  }
}

// ========================================
// CHANGE LOG
// ========================================
// C001: Put id in URL only, omit from request body. What changed: In updateAccessControl(), id is read from accessControl.id and used in the PUT URL (${this.URL}/accesscontrol/access/${id}). The request body is a copy of accessControl with id removed (body = { ...accessControl }; delete body['id']). Previous behavior: Full accessControl object was sent as body, including id; URL used accessControl.id. New behavior: URL still uses id (e.g. .../accesscontrol/access/1007); body does not contain id. Reason: Backend requires id in the URL path but must not receive id in the request body. Context: User clarification that id must be sent in the URL. Impact: Update calls use correct URL and body shape. - 2026-01-27 - HE
// C002: Normalize portalRegisterId[].id to number before PUT accesscontrol/access. What changed: Added private normalizePortalRegisterIdForApi(); createAccessControl() and updateAccessControl() assign body.portalRegisterId from that helper so each element is { id: number }. Previous behavior: Gid access form emitted portalRegisterId: [{ id: "9" }] (string from mat-select), causing PADM ERROR #9406 Unexpected format for value of field id (class base mapping). New behavior: Payload sends id as JSON number (e.g. { id: 9 }). Reason: InterSystems/class mapping expects integer id for portal register reference. Context: User cannot add portal on padmdev (User Access / AddPortal). Impact: Create and update user access calls succeed when portal id was bound as string. - 2026-03-30 - HE
// C003: Restore portalRegisterId on create access body. What changed: createAccessControl() PUT body again includes portalRegisterId from normalizePortalRegisterIdForApi(accessControl.portalRegisterId). updateAccessControl() again sets body.portalRegisterId after spread+delete id. Previous behavior: portalRegisterId was commented out in create body only; PADM put-new-access-control / NewAccessControlRequest returned ERROR #9405 Required field not present portalRegisterId (%JSONImportInternal NewAccessControlRequest). New behavior: Create matches OpenAPI NewAccessControlRequest including portalRegisterId. Reason: portaladmin Add Portal save failed on UAT. Impact: All apps using UserAccessControlService get correct create payload. - 2026-04-15 - HE
// C004: Update PUT omit empty portalRegisterId; normalize drops NaN ids. What changed: normalizePortalRegisterIdForApi() filters with Number.isFinite(row.id). updateAccessControl() assigns body.portalRegisterId only when normalized length > 0, else delete body['portalRegisterId']. Previous behavior: Missing portalRegisterId on emitted object became []; PADM UAT returned 500 errors code 9404 on accesscontrol/access/{id}. New behavior: Empty or invalid ids are not sent as []; callers that emit portal ids get normalized array. Reason: Edit GID access had no portalRegisterId form control; service forced []. Impact: Update without explicit portalRegisterId no longer sends empty array; portaladmin saveForm C014 supplies ids on edit. - 2026-04-15 - HE
